import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SecurityEvent {
  eventType: string;
  resourceType?: string;
  resourceId?: string;
  actionType?: string;
  metadata?: Record<string, any>;
}

export async function logSecurityEvent({
  eventType,
  resourceType,
  resourceId,
  actionType,
  metadata
}: SecurityEvent) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    const { error } = await supabase.rpc('log_security_event', {
      p_event_type: eventType,
      p_user_id: user.id,
      p_resource_type: resourceType || null,
      p_resource_id: resourceId || null,
      p_action_type: actionType || null,
      p_success: true,
      p_error_message: null,
      p_metadata: metadata || {}
    });

    if (error) throw error;
  } catch (error) {
    console.error('Failed to log security event:', error);
    toast.error('Sikkerhetshendelse kunne ikke logges');
  }
}

export async function encryptSensitiveData(data: string): Promise<string> {
  try {
    const { data: result, error } = await supabase.functions.invoke('encrypt-sensitive-data', {
      body: { data }
    });
    
    if (error) throw error;
    return result.encryptedData;
  } catch (error) {
    console.error('Encryption failed:', error);
    toast.error('Kryptering feilet');
    throw error;
  }
}

export function anonymizeData(data: string): string {
  // Basic anonymization - replace sensitive patterns
  return data
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[EMAIL]')
    .replace(/\b\d{11}\b/g, '[PERSONNUMMER]')
    .replace(/\b(?:\+?47)?(?:4|9)\d{7}\b/g, '[TELEFON]');
}

export async function validateDataAccess(resourceType: string, resourceId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    await logSecurityEvent({
      eventType: 'access_check',
      resourceType,
      resourceId,
      actionType: 'validate_access'
    });

    // Add specific access validation logic here
    return true;
  } catch (error) {
    console.error('Access validation failed:', error);
    return false;
  }
}